// 描画済みチャートの <svg> を依存ライブラリなしで PNG 画像として書き出す。
// 取得済み DOM の読み取りのみで、データ・API リクエスト・系列定義には一切触れない。
// （SVG を直列化 → Image → canvas → 凡例を上部に合成 → toBlob → ダウンロード）

// 画像化対象の <svg> を選ぶ。ツールバーの MUI アイコン（<svg class="MuiSvgIcon-root">）を
// 誤って掴まないよう、recharts の描画面を優先し、無ければ MUI アイコン以外の <svg> を選ぶ。
export function findChartSvg(container: HTMLElement): SVGSVGElement | null {
    return (
        container.querySelector<SVGSVGElement>("svg.recharts-surface") ??
        container.querySelector<SVGSVGElement>("svg:not(.MuiSvgIcon-root)") ??
        container.querySelector<SVGSVGElement>("svg")
    );
}

export type LegendItem = { color: string; label: string; value: string };
export type LegendData = { indexLabel: string | null; items: LegendItem[] };

// HoverLegend が付与した data 属性から凡例（index 見出し・色・ラベル・値）を読む。
// 凡例は HTML（SVG 外）のため svg 直列化には乗らない。ここで構造化して取り出す。
export function extractLegend(container: HTMLElement): LegendData {
    const root = container.querySelector<HTMLElement>("[data-chart-legend]");
    if (!root) return { indexLabel: null, items: [] };
    const items = Array.from(root.querySelectorAll<HTMLElement>("[data-legend-item]")).map((el) => ({
        color: el.getAttribute("data-legend-color") || "#000000",
        label: el.getAttribute("data-legend-label") || "",
        value: el.getAttribute("data-legend-value") || "",
    }));
    return { indexLabel: root.getAttribute("data-legend-index"), items };
}

// 文字幅計測に必要な最小インターフェース（CanvasRenderingContext2D と差し替え可能でテストしやすい）。
export type TextMeasurer = { font: string; measureText(text: string): { width: number } };

type LegendDrawOp =
    | { kind: "rect"; x: number; y: number; w: number; h: number; color: string }
    | { kind: "text"; x: number; y: number; text: string; font: string; color: string };

export type LegendLayout = { height: number; ops: LegendDrawOp[] };

const LEGEND_PAD = 8;
const LEGEND_SWATCH = 10;
const LEGEND_SWATCH_GAP = 5;
const LEGEND_ITEM_GAP = 16;
const LEGEND_LINE = 16;
const LEGEND_HEADER_FONT = "bold 12px sans-serif";
const LEGEND_ITEM_FONT = "11px sans-serif";

// 凡例をグラフ上部に折り返しレイアウトし、描画命令列と占有高さを返す。
// 計測のみ行い実描画はしないので、最終 canvas の高さ確定に先立って呼べる。
export function computeLegendLayout(measure: TextMeasurer, legend: LegendData, width: number): LegendLayout {
    const ops: LegendDrawOp[] = [];
    const hasContent = legend.indexLabel != null || legend.items.length > 0;
    if (!hasContent) return { height: 0, ops };

    let y = LEGEND_PAD;
    if (legend.indexLabel != null) {
        ops.push({ kind: "text", x: LEGEND_PAD, y: y + 12, text: `Index: ${legend.indexLabel}`, font: LEGEND_HEADER_FONT, color: "#555555" });
        y += LEGEND_LINE;
    }

    measure.font = LEGEND_ITEM_FONT;
    let x = LEGEND_PAD;
    for (const it of legend.items) {
        const text = `${it.label}: ${it.value}`;
        const itemW = LEGEND_SWATCH + LEGEND_SWATCH_GAP + measure.measureText(text).width;
        // 右端を超えたら折り返す（行頭の 1 個目は溢れても改行しない）。
        if (x > LEGEND_PAD && x + itemW > width - LEGEND_PAD) {
            x = LEGEND_PAD;
            y += LEGEND_LINE;
        }
        ops.push({ kind: "rect", x, y: y + (LEGEND_LINE - LEGEND_SWATCH) / 2, w: LEGEND_SWATCH, h: LEGEND_SWATCH, color: it.color });
        ops.push({ kind: "text", x: x + LEGEND_SWATCH + LEGEND_SWATCH_GAP, y: y + 12, text, font: LEGEND_ITEM_FONT, color: "#222222" });
        x += itemW + LEGEND_ITEM_GAP;
    }

    // 最終行の高さ + 下余白（グラフとの間隔）。
    return { height: y + LEGEND_LINE + LEGEND_PAD, ops };
}

export async function exportChartPng(container: HTMLElement, filename: string): Promise<void> {
    const svg = findChartSvg(container);
    if (!svg) throw new Error("グラフが見つかりません");

    const rect = svg.getBoundingClientRect();
    const width = svg.viewBox?.baseVal?.width || rect.width || 800;
    const height = svg.viewBox?.baseVal?.height || rect.height || 400;

    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute("width", String(width));
    clone.setAttribute("height", String(height));
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");

    // 透過だと暗背景テーマで見づらいため白背景を敷く。
    const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    bg.setAttribute("width", "100%");
    bg.setAttribute("height", "100%");
    bg.setAttribute("fill", "#ffffff");
    clone.insertBefore(bg, clone.firstChild);

    const xml = new XMLSerializer().serializeToString(clone);
    const svgUrl = URL.createObjectURL(new Blob([xml], { type: "image/svg+xml;charset=utf-8" }));

    try {
        const img = new Image();
        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error("画像化に失敗しました"));
            img.src = svgUrl;
        });

        const scale = 2; // 高解像度で書き出す
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("canvas が利用できません");

        // 凡例（色・ラベル・値）をグラフ上部に合成する。canvas 寸法確定前に高さを計測する。
        const legend = computeLegendLayout(ctx, extractLegend(container), width);

        canvas.width = Math.round(width * scale);
        canvas.height = Math.round((height + legend.height) * scale);
        ctx.scale(scale, scale);

        // 凡例領域も含め全面を白で塗る（凡例部の透過を防ぐ）。
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height + legend.height);

        ctx.textBaseline = "alphabetic";
        for (const op of legend.ops) {
            if (op.kind === "rect") {
                ctx.fillStyle = op.color;
                ctx.fillRect(op.x, op.y, op.w, op.h);
            } else {
                ctx.font = op.font;
                ctx.fillStyle = op.color;
                ctx.fillText(op.text, op.x, op.y);
            }
        }

        // グラフ本体は凡例の下に描く。
        ctx.drawImage(img, 0, legend.height, width, height);

        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
        if (!blob) throw new Error("PNG の生成に失敗しました");

        const dlUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = dlUrl;
        a.download = filename.endsWith(".png") ? filename : `${filename}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(dlUrl);
    } finally {
        URL.revokeObjectURL(svgUrl);
    }
}
